import { useEffect, useMemo, useState } from "react";
import { groupSigs } from "../types/api";
import type { LookalikeLeg, ManualMatchPair, RowPin, SaveCheck } from "../types/api";
import { useColumnWidths } from "./useColumnWidths";

// Asked before hand matches are saved for future months, and only when it applies
// (backend/manual_save_check.py): first any OLDER saved match that would claim one of the
// rows (only the first saved match naming a row ever applies), then any ticked row with
// look-alikes, where finance names the one detail that will stay the same next month.

export interface SaveAnswers {
  // Per pair, index-aligned with the pairs passed in.
  pins: Record<string, RowPin>[];
  replace: ManualMatchPair[][];
}

interface Props {
  pairs: ManualMatchPair[];
  checks: SaveCheck[];
  onConfirm: (answers: SaveAnswers) => void;
  onCancel: () => void;
}

// "CRI|PJ/1|ALPHA|DEBITNOTE|32.86~D:..." -> "PJ/1 · ALPHA · DEBITNOTE · 32.86"
function pretty(key: string): string {
  const base = key.replace(/(~[DRC]:[^~#]*)+(#\d+)?$|#\d+$/, "");
  return base.replace(/^(CRI|INS)\|/, "").split("|").filter(Boolean).join(" · ");
}

function Legs({ pair }: { pair: ManualMatchPair }) {
  const { cri, ins } = groupSigs(pair);
  return (
    <div className="save-pair">
      <div className="save-pair-col">
        <div className="save-side save-side--cri">CRI</div>
        {cri.length ? cri.map((s) => <div key={s} className="save-leg">{pretty(s)}</div>)
          : <div className="save-leg muted">—</div>}
      </div>
      <div className="save-pair-col">
        <div className="save-side save-side--ins">INSURER</div>
        {ins.length ? ins.map((s) => <div key={s} className="save-leg">{pretty(s)}</div>)
          : <div className="save-leg muted">—</div>}
      </div>
    </div>
  );
}

// "" = "I'm not sure, save as today"; undefined = not answered yet.
type Choice = string | undefined;

// A label mid-sentence: "Due date" -> "due date", but "CRI voucher number" keeps its acronym.
const inSentence = (label: string) => (/^[A-Z]{2}/.test(label) ? label : label.charAt(0).toLowerCase() + label.slice(1));

function LookalikeSection({ leg, choice, onChoose, name }: {
  leg: LookalikeLeg; choice: Choice; onChoose: (c: string) => void; name: string;
}) {
  const cols = Object.keys(leg.labels);
  const side = leg.side === "cri" ? "CRI" : "insurer";
  const picked = leg.columns.find((c) => c.column === choice);
  const cw = useColumnWidths(useMemo(() => ["__mine", ...cols], [leg]), leg); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="save-section">
      <div className="save-section-head">
        <span>Your {side} row · {pretty(leg.key)}</span>
        <span className="badge badge-warn">{leg.rows.length - 1} look-alike{leg.rows.length === 2 ? "" : "s"}</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table className={"data-table" + cw.className} style={cw.style}>
          {cw.colgroup}
          <thead>
            <tr><th>{cw.grip("__mine")}</th>
              {cols.map((c) => <th key={c} className={c === choice ? "save-hl" : ""}>{leg.labels[c]}{cw.grip(c)}</th>)}</tr>
          </thead>
          <tbody>
            {leg.rows.map((r, i) => (
              <tr key={i} className={r.yours ? "save-mine" : ""}>
                <td>{r.yours ? <span className="badge badge-muted">✓ Your row</span>
                  : <span className="muted">Look-alike</span>}</td>
                {cols.map((c) => <td key={c} className={c === choice ? "save-hl" : ""}>{r.values[c] || "—"}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="save-question">Which detail will still be the same on this transaction next month?</div>
      <div className="save-cards" role="radiogroup" aria-label="Which detail will still be the same next month">
        {leg.columns.map((c) => (
          <label key={c.column} className={`save-card${choice === c.column ? " save-card--on" : ""}`}>
            <input type="radio" name={name} checked={choice === c.column} onChange={() => onChoose(c.column)} />
            <span>
              <strong>{c.label}</strong>
              <span className="muted save-card-sub">
                Yours: <strong>{c.yours}</strong> · look-alike{c.others.length === 1 ? "" : "s"}: {c.others.join(", ")}
              </span>
            </span>
          </label>
        ))}
        <label className={`save-card${choice === "" ? " save-card--on" : ""}`}>
          <input type="radio" name={name} checked={choice === ""} onChange={() => onChoose("")} />
          <span>
            <strong>I'm not sure, save as today</strong>
            <span className="muted save-card-sub">
              If the look-alikes are still open next month, the match won't be applied and you'll be asked to tick it again.
            </span>
          </span>
        </label>
      </div>
      {!leg.columns.length && (
        <p className="muted save-say">No single detail tells your row apart from every look-alike, so it is saved as today.</p>
      )}
      <div className="save-say" aria-live="polite">
        {choice === undefined
          ? "Choose an answer above to see what the app will look for next month."
          : picked
            ? <>Next month the app will look for the {side} row whose <strong>{inSentence(picked.label)}</strong> is{" "}
                <strong>{picked.yours}</strong> among these look-alikes. If it can't find exactly one, it won't apply the
                match and will tell you why.</>
            : "Saved as today."}
      </div>
    </div>
  );
}

export default function SaveMatchDialog({ pairs, checks, onConfirm, onCancel }: Props) {
  const conflicts = useMemo(() => checks.some((c) => c.conflicts.length), [checks]);
  const [step, setStep] = useState<"conflict" | "lookalike">(conflicts ? "conflict" : "lookalike");
  // choices[pairIndex][legKey]
  const [choices, setChoices] = useState<Record<number, Record<string, Choice>>>({});

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onCancel(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onCancel]);

  const legs = checks.flatMap((c, i) => c.lookalikes.map((leg) => ({ i, leg })));
  // Every look-alike needs an answer: a row with no detail to offer is saved as today.
  const answered = legs.every(({ i, leg }) => !leg.columns.length || choices[i]?.[leg.key] !== undefined);
  const nReplace = new Set(checks.flatMap((c) => c.conflicts.map((p) => JSON.stringify(groupSigs(p))))).size;

  function finish() {
    onConfirm({
      replace: checks.map((c) => c.conflicts),
      pins: checks.map((c, i) => {
        const out: Record<string, RowPin> = {};
        for (const leg of c.lookalikes) {
          const col = choices[i]?.[leg.key];
          const hit = leg.columns.find((x) => x.column === col);
          if (hit) out[leg.key] = { column: hit.column, value: hit.yours };
        }
        return out;
      }),
    });
  }

  return (
    <div className="modal-backdrop" role="presentation">
      <div className="modal save-modal" role="dialog" aria-modal="true" aria-labelledby="smd-title">
        {step === "conflict" ? (
          <>
            <h3 id="smd-title" style={{ marginTop: 0 }}>
              {nReplace === 1 ? "This row is already in a saved match" : "These rows are already in saved matches"}
            </h3>
            <p className="muted" style={{ marginTop: 0 }}>
              A saved match is re-applied every month. Two saved matches can't claim the same row: only the oldest
              would apply, and the others would silently stop working. Replace the older ones with your new match, or cancel.
            </p>
            {pairs.map((p, i) => !checks[i].conflicts.length ? null : (
              <div key={i}>
                <div className="save-label">You are saving</div>
                <Legs pair={p} />
                <div className="save-label">
                  {checks[i].conflicts.length === 1 ? "Older saved match that names" : "Older saved matches that name"} the same row{" "}
                  <span className="badge badge-risk">will be removed</span>
                </div>
                {checks[i].conflicts.map((c, k) => <Legs key={k} pair={c} />)}
                {checks[i].conflicts.some((c) => groupSigs(c).cri.length + groupSigs(c).ins.length > 2) && (
                  <div className="save-callout">
                    Replacing removes each older saved match <strong>whole</strong>, including any other rows it holds.
                    Re-match those rows separately if that part is still right.
                  </div>
                )}
              </div>
            ))}
            <div className="modal-actions">
              <button className="btn" onClick={onCancel}>Cancel</button>
              <button className="btn btn-primary" onClick={() => (legs.length ? setStep("lookalike") : finish())}>
                Replace {nReplace} older saved match{nReplace === 1 ? "" : "es"}
              </button>
            </div>
          </>
        ) : (
          <>
            <h3 id="smd-title" style={{ marginTop: 0 }}>Help the app find this row again next month</h3>
            <p className="muted" style={{ marginTop: 0 }}>
              Saved matches are re-applied automatically every month. A row you ticked has look-alikes with the same
              reference, party, type and amount, so the app needs one more detail to be sure it picks <strong>your</strong> row
              next time.
            </p>
            {legs.map(({ i, leg }) => (
              <LookalikeSection key={`${i}:${leg.key}`} leg={leg} name={`pin-${i}-${leg.key}`}
                choice={choices[i]?.[leg.key]}
                onChoose={(col) => setChoices((prev) => ({ ...prev, [i]: { ...(prev[i] ?? {}), [leg.key]: col } }))} />
            ))}
            <div className="modal-actions">
              <button className="btn" onClick={onCancel}>Cancel</button>
              <button className="btn btn-primary" disabled={!answered} onClick={finish}>Save match</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
